import {
  artccIdSelector,
  colorNameMap,
  edstFontDim,
  getBitmapTextStyles,
  ownedFavIdsSelector,
  TBE,
  TBP,
  useOnUnmount,
  useStableCallback,
} from "@poscon/shared-frontend";
import {
  FlightplanId,
  FlightplanTrackPosition,
  formatAssignedAltitude,
  includes,
  MtcaConflict,
  MtcaParticipant,
} from "@poscon/shared-types";
import {
  Coordinate,
  EdstFpData,
  EramCoordination,
  EramFlightplan,
  EramLocalCoordination,
  EramTrack,
  getFieldBAndC,
  getFpInSectorTime,
  GpdField,
  positionSymbolMap,
} from "@poscon/shared-types/eram";
import { ColorSource, Container, FederatedPointerEvent, Graphics } from "pixi.js";
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { lineSliceAlong, lineString, length } from "@turf/turf";
import { GpdGeoState } from "~/gpdStore";
import { useDragging } from "~/hooks/useDragging";
import { useRootDispatch, useRootSelector } from "~/redux/hooks";
import {
  flightplanSelector,
  trackCoordinationDataSelector,
  trackFromFpIdSelector,
} from "~/redux/slices/aircraftSlice";
import { aircraftIsAselSelector } from "~redux/slices/edstSlice";
import {
  EramTrajectoryData,
  forceGpdDisplayFpSelector,
  gpdAircraftDataSelector,
  removeForceShowConflicts,
  setForceShowConflicts,
} from "~/redux/slices/edstFlightplanSlice";
import { aircraftSelect } from "~/redux/thunks/aircraftSelect";
import { openMenuThunk } from "~/redux/thunks/openMenuThunk";
import { EdstAselMenu } from "~/types/edstView";
import { TableField } from "../utils/TableField";
import { GpdState } from "~/redux/slices/gpdSlice";
import { updateEdstFpData } from "~/redux/thunks/processEramMessage";
import { alertRed, alertYellow, mutedAlertRed, mutedAlertYellow } from "~/utils/constants";

const emptyObj = {};

const defaultDatablockPosition = [20, -10] as Coordinate;

const fontDim = edstFontDim;

export type GpdAircraftTrackProps = {
  fpId: FlightplanId;
  geoState: GpdGeoState;
  state: GpdState;
  forcedConflicts: Record<FlightplanId, FlightplanId[]>;
  now: number;
};

export const GpdAircraftTrack = ({ fpId, geoState, state, forcedConflicts, now }: GpdAircraftTrackProps) => {
  const fp = useRootSelector((state) => flightplanSelector(state, fpId));
  const { trajectory, fpTrack, edstFp, conflicts } = useRootSelector((state) =>
    gpdAircraftDataSelector(state, fpId)
  );
  const forceDisplayConflicts = useRootSelector((state) => forceGpdDisplayFpSelector(state, fpId));

  if (state.suppressed && !edstFp.showConflicts && forceDisplayConflicts.length === 0) {
    return null;
  }

  if (!fp || !trajectory || !fpTrack) {
    console.log(`Could not render GPD aircraft track for FP ID ${fpId}(${fp?.callsign}): missing data`);
    return null;
  }

  let pos = geoState.getPixelCoordFromLonLat(fpTrack.position);

  return pos ? (
    <InnerGpdAircraftTrack
      state={geoState}
      pos={pos}
      fp={fp}
      trajectory={trajectory}
      fpTrack={fpTrack}
      edstFp={edstFp}
      forcedConflicts={forcedConflicts}
      conflicts={conflicts}
      now={now}
    />
  ) : null;
};

type InnerGpdAircraftTrackProps = {
  state: GpdGeoState;
  pos: Coordinate;
  fp: EramFlightplan;
  trajectory: EramTrajectoryData;
  fpTrack: FlightplanTrackPosition;
  edstFp: EdstFpData;
  forcedConflicts: Record<FlightplanId, FlightplanId[]>;
  conflicts: Array<MtcaConflict>;
  now: number;
};

const InnerGpdAircraftTrack = ({
  state,
  pos,
  fp,
  trajectory,
  fpTrack,
  edstFp,
  forcedConflicts,
  conflicts,
  now,
}: InnerGpdAircraftTrackProps) => {
  const fpId = fp.id;
  const dispatch = useRootDispatch();
  const track = useRootSelector((state) => trackFromFpIdSelector(state, fp.id));

  const coordination = useRootSelector((state) =>
    track
      ? trackCoordinationDataSelector(state, track.id)
      : (emptyObj as EramCoordination & EramLocalCoordination)
  );

  const artccId = useRootSelector(artccIdSelector);
  const ownedFavIds = useRootSelector(ownedFavIdsSelector);

  const showConflicts = edstFp.showConflicts;

  const relevantConflicts = useMemo(() => {
    const times =
      trajectory.favCrossings && artccId
        ? getFpInSectorTime(trajectory.favCrossings, artccId, ownedFavIds)
        : { minTime: 0, maxTime: 0 };

    const forceDisplayConflicts = Object.entries(forcedConflicts).flatMap(([k, fpIds]) => {
      return fpIds.includes(fpId) ? [k] : [];
    });

    return conflicts
      .filter((p) => {
        const [c, other] = p.conflictA.fpId === fp.id ? [p.conflictA, p.conflictB] : [p.conflictB, p.conflictA];

        return showConflicts === "all" ||
          includes(forceDisplayConflicts, other.fpId) ||
          (p.affectedFavs.some((favId) => ownedFavIds.includes(favId)) && (c.segment.endTime >= Math.max(now, times.minTime) && c.segment.startTime <= times.maxTime))
      });
  }, [conflicts, forcedConflicts, trajectory.favCrossings, artccId, ownedFavIds, showConflicts, now]);

  useEffect(() => {
    if (edstFp.showConflicts) {
      const newFpIds = relevantConflicts
        .flatMap((p) =>
          [p.conflictA.fpId, p.conflictB.fpId]
        )
        .filter((id) => id !== fpId);

      if (new Set(...(forcedConflicts[fpId] ?? [])).symmetricDifference(new Set(...newFpIds)).size > 0) {
        dispatch(
          setForceShowConflicts({
            key: fpId,
            fpIds: newFpIds,
          })
        );
      }
    } else {
      dispatch(removeForceShowConflicts(fpId));
    }
  }, [fpId, edstFp.showConflicts, conflicts, forcedConflicts, dispatch]);

  useOnUnmount(() => {
    dispatch(removeForceShowConflicts(fpId));
  });

  const forceDisplayConflicts = Object.entries(forcedConflicts).flatMap(([k, fpIds]) => {
    return fpIds.includes(fpId) ? [k] : [];
  });

  let tint: ColorSource = colorNameMap.white;

  const clearedAlt =
    coordination?.localInterimAltitude ??
    coordination?.interimAltitude?.altitude ??
    fp.assignedAltitude?.simple;

  const conflictParts = relevantConflicts.map((conflict) => conflict.conflictA.fpId === fp.id ? conflict.conflictA : conflict.conflictB);

  if (edstFp.showConflicts || forceDisplayConflicts.length > 0) {
    if (conflicts.length === 0) {
      tint = colorNameMap.green;
    } else if (conflictParts.some((c) => c.segment.minimumDistance <= 5)) {
      tint =
        clearedAlt &&
          conflictParts.some((c) =>
            c.segment.minimumDistance <= 5 && c.segment.minAltitude - 500 <= clearedAlt && c.segment.maxAltitude + 500 <= clearedAlt
          )
          ? alertRed
          : mutedAlertRed;
    } else {
      tint =
        clearedAlt &&
          conflictParts.some((c) =>
            c.segment.minAltitude - 500 <= clearedAlt && c.segment.maxAltitude + 500 >= clearedAlt
          )
          ? alertYellow
          : mutedAlertYellow;
    }
  }

  return (
    <>
      {(edstFp.showConflicts || forceDisplayConflicts.length > 0) && (
        <GpdTrajectory
          state={state}
          fpTrack={fpTrack}
          trajectory={trajectory}
          conflicts={conflictParts}
          tint={tint}
        />
      )}
      <container roundPixels x={Math.round(pos[0])} y={Math.round(pos[1])} zIndex={2}>
        <bitmapText
          text={positionSymbolMap.flatTrack}
          x={-6}
          y={-8}
          eventMode="static"
          style={{ ...getBitmapTextStyles("eramPositionSymbols1"), fill: tint }}
          onMouseDown={(e: FederatedPointerEvent) => {
            if (e.button === TBE) {
              dispatch(updateEdstFpData(fp.id, { showConflicts: !showConflicts ? "sector" : null }));
            }
          }}
        />
        <GpdDatablock
          edstFp={edstFp}
          track={track}
          coordination={coordination}
          fp={fp}
          conflicts={conflicts}
          tint={tint}
        />
      </container>
    </>
  );
};

type GpdTrajectoryProps = {
  fpTrack: FlightplanTrackPosition;
  trajectory: EramTrajectoryData;
  conflicts: Array<MtcaParticipant>;
  state: GpdGeoState;
  tint: ColorSource;
};

const GpdTrajectory = ({ state, fpTrack, trajectory, conflicts, tint }: GpdTrajectoryProps) => {
  const remainingTrajectory = useMemo(() => {
    const line = lineString(trajectory.points.map((p) => p.position));
    const distAlong = fpTrack.distanceAlongRoute / 1000; // Convert to km
    const lineLength = length(line);

    const remainingTrajectory = distAlong < lineLength ? lineSliceAlong(line, distAlong, lineLength) : null;
    if (!remainingTrajectory || remainingTrajectory.geometry.coordinates.length < 2) {
      return null;
    }
    return remainingTrajectory;
  }, [trajectory, fpTrack.distanceAlongRoute]);

  const draw = useCallback(
    (graphics: Graphics) => {
      graphics.clear();

      if (!remainingTrajectory || remainingTrajectory.geometry.coordinates.length < 2) {
        return;
      }

      graphics.setStrokeStyle({
        width: 2,
        alpha: 1,
        color: tint,
        alignment: 0.5,
      });
      const start = state.getPixelCoordFromLonLat(remainingTrajectory.geometry.coordinates[0] as Coordinate);
      graphics.moveTo(start[0], start[1]);
      for (const coord of remainingTrajectory.geometry.coordinates) {
        const pixelCoord = state.getPixelCoordFromLonLat(coord as Coordinate);
        graphics.lineTo(pixelCoord[0], pixelCoord[1]);
      }
      graphics.stroke();
      graphics.setStrokeStyle({
        width: 6,
        alpha: 1,
        color: tint,
        alignment: 0.5,
      });
      for (const segment of conflicts.map((c) => c.segment)) {
        const coords = segment.line.map((p) => p.coord);
        if (coords.length < 2) {
          continue;
        }
        const start = state.getPixelCoordFromLonLat(coords[0]!);
        graphics.moveTo(start[0], start[1]);
        for (const p of coords.slice(1)) {
          const pixelCoord = state.getPixelCoordFromLonLat(p);
          if (pixelCoord) {
            graphics.lineTo(pixelCoord[0], pixelCoord[1]);
          }
        }
        graphics.stroke();
      }
    },
    [state, remainingTrajectory, tint, conflicts]
  );

  const remainingPoints = !remainingTrajectory
    ? []
    : trajectory.points.slice(
      trajectory.points.length - remainingTrajectory.geometry.coordinates.length + 1,
      trajectory.points.length
    );

  return (
    <>
      <graphics roundPixels draw={draw} zIndex={0} />
      {remainingPoints.map((p) => {
        if (p.restrictionName) {
          const coord = state.getPixelCoordFromLonLat(p.position);
          if (!coord) {
            return null;
          }
          return (
            <bitmapText
              key={p.restrictionName}
              eventMode="none"
              x={coord[0] - Math.floor(fontDim.width / 2)}
              y={coord[1] - Math.floor(fontDim.height / 2)}
              zIndex={1}
              text={`*${(p.altitude / 100).toFixed(0)}`}
              style={{ ...getBitmapTextStyles("eramText2"), fill: colorNameMap.white }}
            />
          );
        }
        return null;
      })}
    </>
  );
};

const datablockWidth = fontDim.width * 7;

type GpdDatablockProps = {
  fp: EramFlightplan;
  track: EramTrack | null;
  edstFp: EdstFpData;
  coordination: EramCoordination & EramLocalCoordination;
  conflicts: Array<MtcaConflict>;
  tint?: ColorSource;
};

const GpdDatablock = ({ fp, edstFp, coordination, track, conflicts, tint }: GpdDatablockProps) => {
  const ref = useRef<Container>(null);
  const asel = useRootSelector((state) => aircraftIsAselSelector(state, fp.id));

  const dispatch = useRootDispatch();
  // TODO: clip datablock pos to GPD view bounds
  const [datablockPosition, setDatablockPosition] = useState(
    edstFp.gpdDatablockPosition ?? defaultDatablockPosition
  );
  const mouseDownRef = useRef(false);

  const level1Conflicts = [];
  const level2Conflicts = [];

  for (const conflict of conflicts) {
    let c = conflict.conflictA.fpId === fp.id ? conflict.conflictA : conflict.conflictB
    if (c.segment.minimumDistance <= 5 || c.segment.minimumDistance <= 5) {
      level1Conflicts.push(c);
    } else {
      level2Conflicts.push(c);
    }
  }

  const { startDrag, anyDragging, isDraggingRef } = useDragging(ref, null, "mouseup", false, (pos) => {
    if (ref.current) {
      const globalPos = ref.current.getGlobalPosition();
      const posDiff = [pos.x - globalPos.x, pos.y - globalPos.y] satisfies Coordinate;
      const newPos = [
        Math.round(datablockPosition[0] + posDiff[0]),
        Math.round(datablockPosition[1] + posDiff[1]),
      ] satisfies Coordinate;
      setDatablockPosition(newPos);
      dispatch(updateEdstFpData(fp.id, { gpdDatablockPosition: newPos }));
    }
  });

  useEffect(() => {
    if (edstFp.gpdDatablockPosition) {
      setDatablockPosition(edstFp.gpdDatablockPosition);
    }
  }, [edstFp.gpdDatablockPosition]);

  const isSelected = (field: GpdField) => {
    return asel?.fpId === fp.id && asel?.field === field;
  };

  const handleFieldMouseDown = useStableCallback(
    (element: Container, field: GpdField, menu?: EdstAselMenu, menuOffset?: Coordinate) => {
      if (isDraggingRef.current) {
        return;
      }
      dispatch(aircraftSelect(fp.id, field, menu));
      mouseDownRef.current = false;
      if (menu && !isSelected(field)) {
        dispatch(openMenuThunk(menu, element, menuOffset));
      }
    }
  );

  const fieldBAndC = track ? getFieldBAndC(track, coordination, fp.assignedAltitude) : null;

  const line2 = fieldBAndC
    ? `${fieldBAndC.fieldBText}${fieldBAndC.fieldB4Text}${fieldBAndC.fieldCText}`
    : "XXX";

  const x = Math.round(datablockPosition[0]);
  const y = Math.round(datablockPosition[1]);

  const leaderLineAnchorX = Math.round(datablockPosition[0] + datablockWidth < 0 ? datablockWidth + 2 : -2);

  const clearedAlt =
    coordination?.localInterimAltitude ??
    coordination?.interimAltitude?.altitude ??
    fp.assignedAltitude?.simple;

  const level1Tint =
    clearedAlt &&
      level1Conflicts.some((c) =>
        clearedAlt <= c.segment.maxAltitude + 500 && c.segment.minAltitude - 500 <= clearedAlt
      )
      ? alertRed
      : mutedAlertRed;
  const level2Tint =
    clearedAlt &&
      level2Conflicts.some((c) =>
        clearedAlt <= c.segment.maxAltitude + 500 && c.segment.minAltitude - 500 <= clearedAlt

      )
      ? alertYellow
      : mutedAlertYellow;

  return (
    <container roundPixels x={x} y={y} sortableChildren>
      <graphics
        eventMode="none"
        draw={(graphics) => {
          graphics.clear();
          graphics.moveTo(leaderLineAnchorX, 1).lineTo(-x, -y).stroke({ width: 1, color: tint });
        }}
      />
      {level1Conflicts.length > 0 && (
        <bitmapText
          text={level1Conflicts.length.toString()}
          x={0}
          y={-(fontDim.height - 2)}
          style={{ ...getBitmapTextStyles("eramText2"), fill: level1Tint }}
        />
      )}
      {level2Conflicts.length > 0 && (
        <bitmapText
          text={level2Conflicts.length.toString()}
          x={fontDim.width * 3}
          y={-(fontDim.height - 2)}
          style={{ ...getBitmapTextStyles("eramText2"), fill: level2Tint }}
        />
      )}
      <container
        ref={ref}
        sortableChildren
        zIndex={1}
        eventMode={anyDragging ? "none" : "static"}
        onMouseDown={(e: FederatedPointerEvent) => {
          if (e.button === TBP) {
            mouseDownRef.current = true;
          }
          if (e.button === TBE) {
            dispatch(updateEdstFpData(fp.id, { showConflicts: edstFp.showConflicts ? null : "sector" }));
          }
        }}
        onMouseMove={(e: FederatedPointerEvent) => {
          if (!anyDragging && mouseDownRef.current) {
            startDrag({ ...e, button: TBP });
          }
          mouseDownRef.current = false;
        }}
      >
        <TableField
          key="CALLSIGN"
          tint={tint}
          text={fp.callsign}
          selected={isSelected("CALLSIGN_GPD_FIELD")}
          onmouseup={(event) => {
            if (event.button === TBP) {
              handleFieldMouseDown(event.currentTarget as Container, "CALLSIGN_GPD_FIELD");
            }
          }}
        />
        {line2 && (
          <TableField
            key="ALT"
            y={fontDim.height + 2}
            tint={tint}
            text={line2}
            selected={isSelected("ALT_GPD_FIELD")}
            onmouseup={(event) => {
              if (event.button === TBP) {
                handleFieldMouseDown(event.currentTarget as Container, "ALT_GPD_FIELD", "ALTITUDE_MENU", [
                  fontDim.width * 8,
                  0,
                ]);
              }
            }}
          />
        )}
        <TableField
          key="DEST"
          y={fontDim.height * 2 + 4}
          tint={tint}
          text={fp.destination}
          selected={isSelected("ROUTE_GPD_FIELD")}
          onmouseup={(event) => {
            if (event.button === TBP) {
              handleFieldMouseDown(event.currentTarget as Container, "ROUTE_GPD_FIELD", "ROUTE_MENU");
            }
          }}
        />
        {track?.target && (
          <TableField
            key="GS"
            x={fontDim.width * (fp.destination.length + 1)}
            y={fontDim.height * 2 + 4}
            tint={tint}
            text={Math.round(track.target.groundSpeed).toString()}
            selected={isSelected("GROUNDSPEED_GPD_FIELD")}
            onmouseup={(event) => {
              if (event.button === TBP) {
                handleFieldMouseDown(event.currentTarget as Container, "GROUNDSPEED_GPD_FIELD", "SPEED_MENU");
              }
            }}
          />
        )}
        {(coordination.interimAltitude || coordination.localInterimAltitude || line2 === "XXX") &&
          fp.assignedAltitude && (
            <TableField
              x={0}
              y={fontDim.height * 3 + 4}
              tint={tint}
              text={formatAssignedAltitude(fp.assignedAltitude)}
            />
          )}
      </container>
    </container>
  );
};
